import React from 'react';
import { ClipboardList, Pencil, Trash2, CheckSquare, Circle } from 'lucide-react';
import { Card, Badge, LoadingSpinner } from '../common';
import { Category, AdminTestQuestion } from '../../types';

interface DeleteItem {
  type: 'subcategory' | 'product' | 'video' | 'testQuestion';
  id: string;
  name: string;
  categoryId?: string;
  subcategoryId?: string;
}

interface TestsTabProps {
  categories: Category[];
  testQuestions: AdminTestQuestion[];
  selectedCategoryId: string;
  isLoading: boolean;
  onSelectCategory: (id: string) => void;
  onEdit: (question: AdminTestQuestion) => void;
  onDelete: (item: DeleteItem) => void;
}

// ─── Tipos internos de normalización ───────────────────────────────────────

interface NormalizedAnswer {
  id: string;
  text: string;
  isCorrect: boolean;
}

interface NormalizedSubQuestion {
  /** ID único para React key */
  key: string;
  /** ID del registro DB padre (para delete) */
  dbRecordId: string;
  dbCategoryId: string;
  dbRecord: AdminTestQuestion;
  question: string;
  mode: 'single' | 'multi' | 'drag_drop' | 'unknown';
  answers: NormalizedAnswer[];
  /** true si viene del formato legacy `options.questions[]` — no editable con modal nuevo */
  isLegacyFormat: boolean;
  /** true si es drag_drop */
  isDragDrop: boolean;
}

// ─── Helpers de parseo ──────────────────────────────────────────────────────

type AnyOptions = {
  mode?: string;
  // formato nuevo
  answers?: Array<{ id: string; text: string }>;
  // formato heredado
  questions?: Array<{
    id?: string;
    title: string;
    answers?: Array<{ id: string; text: string; correct?: boolean }>;
    correct_answer?: string | string[];
  }>;
} | null;

function resolveMode(raw?: string): 'single' | 'multi' | 'unknown' {
  if (raw === 'single') return 'single';
  if (raw === 'multi') return 'multi';
  return 'unknown';
}

/**
 * Expande un registro DB en una lista de sub-preguntas normalizadas.
 *
 * - Formato heredado (`options.questions[]`): produce N filas, una por sub-pregunta.
 * - Formato nuevo (`options.answers[]`): produce 1 fila.
 * - Drag & drop: produce 1 fila sin opciones.
 */
function expandRecord(record: AdminTestQuestion): NormalizedSubQuestion[] {
  if (record.questionType === 'drag_drop') {
    return [{
      key: record.id,
      dbRecordId: record.id,
      dbCategoryId: record.categoryId,
      dbRecord: record,
      question: record.question,
      mode: 'drag_drop',
      answers: [],
      isLegacyFormat: false,
      isDragDrop: true,
    }];
  }

  const opts = record.options as AnyOptions;

  // ── Formato heredado: options.questions[] ──────────────────────────────
  if (Array.isArray(opts?.questions) && opts!.questions!.length > 0) {
    const mode = resolveMode(opts?.mode);
    return opts!.questions!.map((subQ, i) => {
      const answers: NormalizedAnswer[] = (subQ.answers || []).map((a) => {
        // correcto puede venir por `correct: true` en el answer O por `correct_answer` en la sub-pregunta
        let isCorrect = !!a.correct;
        if (!isCorrect && subQ.correct_answer) {
          const ca = subQ.correct_answer;
          if (Array.isArray(ca)) isCorrect = ca.includes(a.id);
          else isCorrect = ca === a.id;
        }
        return { id: a.id, text: a.text, isCorrect };
      });

      return {
        key: `${record.id}_${subQ.id ?? i}`,
        dbRecordId: record.id,
        dbCategoryId: record.categoryId,
        dbRecord: record,
        question: subQ.title,
        mode,
        answers,
        isLegacyFormat: true,
        isDragDrop: false,
      };
    });
  }

  // ── Formato nuevo: options.answers[] + correctAnswer field ─────────────
  if (Array.isArray(opts?.answers)) {
    const mode = resolveMode(opts?.mode);
    const rawCorrect = record.correctAnswer;
    const correctIds: string[] = Array.isArray(rawCorrect)
      ? (rawCorrect as string[]).map(String)
      : typeof rawCorrect === 'string'
      ? [rawCorrect]
      : [];

    const answers: NormalizedAnswer[] = opts!.answers!.map((a) => ({
      id: a.id,
      text: a.text,
      isCorrect: correctIds.includes(a.id),
    }));

    return [{
      key: record.id,
      dbRecordId: record.id,
      dbCategoryId: record.categoryId,
      dbRecord: record,
      question: record.question,
      mode,
      answers,
      isLegacyFormat: false,
      isDragDrop: false,
    }];
  }

  // Fallback: registro sin opciones reconocibles
  return [{
    key: record.id,
    dbRecordId: record.id,
    dbCategoryId: record.categoryId,
    dbRecord: record,
    question: record.question,
    mode: 'unknown',
    answers: [],
    isLegacyFormat: false,
    isDragDrop: false,
  }];
}

// ─── Componente ─────────────────────────────────────────────────────────────

const TestsTab: React.FC<TestsTabProps> = ({
  categories,
  testQuestions,
  selectedCategoryId,
  isLoading,
  onSelectCategory,
  onEdit,
  onDelete,
}) => {
  const selectedCategory = categories.find((c) => c.id === selectedCategoryId);

  // Expande todos los registros DB a sub-preguntas individuales
  const flatQuestions: NormalizedSubQuestion[] = testQuestions.flatMap(expandRecord);

  return (
    <Card title="Tests" className="border border-gray-500/30">
      {/* Selector de categoría */}
      <div className="mb-5">
        <label className="block text-sm font-medium text-gray-900 mb-2">Categoría</label>
        <select
          value={selectedCategoryId}
          onChange={(e) => onSelectCategory(e.target.value)}
          className="w-full sm:w-72 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brinsa-azul text-sm"
        >
          <option value="">Seleccionar categoría...</option>
          {categories.map((c) => (
            <option key={c.id} value={c.id}>{c.name}</option>
          ))}
        </select>
      </div>

      {!selectedCategoryId && (
        <div className="flex flex-col items-center py-12 text-gray-800/50">
          <ClipboardList className="w-12 h-12 mb-3 opacity-40" />
          <p className="text-sm">Selecciona una categoría para ver sus preguntas</p>
        </div>
      )}

      {selectedCategoryId && isLoading && (
        <div className="flex justify-center py-10">
          <LoadingSpinner size="md" />
        </div>
      )}

      {selectedCategoryId && !isLoading && (
        <>
          {/* Contador */}
          {flatQuestions.length > 0 && (
            <p className="text-xs text-gray-800/60 mb-3">
              {flatQuestions.length} pregunta{flatQuestions.length !== 1 ? 's' : ''} en{' '}
              <span className="font-medium text-gray-900">{selectedCategory?.name}</span>
              {testQuestions.length !== flatQuestions.length && (
                <span className="ml-1">({testQuestions.length} registro{testQuestions.length !== 1 ? 's' : ''} en DB)</span>
              )}
            </p>
          )}

          <div className="space-y-3">
            {flatQuestions.length === 0 ? (
              <div className="flex flex-col items-center py-10 text-gray-800/50">
                <ClipboardList className="w-10 h-10 mb-2 opacity-40" />
                <p className="text-sm">
                  No hay preguntas en{' '}
                  <span className="font-medium text-gray-900">{selectedCategory?.name}</span>.
                  Usa "Nueva Pregunta" para crear la primera.
                </p>
              </div>
            ) : (
              flatQuestions.map((subQ, idx) => (
                <div
                  key={subQ.key}
                  className="p-4 border border-gray-500/30 rounded-lg hover:border-gray-900 hover:bg-gray-500/5 transition-all"
                >
                  {/* Cabecera */}
                  <div className="flex items-start justify-between gap-3 mb-3">
                    <div className="flex items-start gap-3 min-w-0">
                      <span className="flex-shrink-0 w-6 h-6 rounded-full bg-gray-900/10 flex items-center justify-center text-xs font-semibold text-gray-900">
                        {idx + 1}
                      </span>
                      <p className="text-sm font-medium text-gray-900 leading-snug">{subQ.question}</p>
                    </div>

                    <div className="flex items-center gap-1 flex-shrink-0">
                      {/* Badge tipo */}
                      {subQ.isDragDrop ? (
                        <Badge variant="default" size="sm">Drag &amp; Drop</Badge>
                      ) : subQ.mode === 'multi' ? (
                        <Badge variant="info" size="sm">Múltiple</Badge>
                      ) : subQ.mode === 'single' ? (
                        <Badge variant="success" size="sm">Única</Badge>
                      ) : (
                        <Badge variant="default" size="sm">Quiz</Badge>
                      )}

                      {/* Indicador formato heredado */}
                      {subQ.isLegacyFormat && (
                        <span className="text-xs text-gray-800/40 ml-1" title="Formato heredado">
                          heredado
                        </span>
                      )}

                      {/* Editar — solo disponible para formato nuevo (1 sub-pregunta por registro) */}
                      <button
                        onClick={() => onEdit(subQ.dbRecord)}
                        disabled={subQ.isLegacyFormat || subQ.isDragDrop}
                        className="ml-2 p-1.5 rounded-lg text-gray-800/60 hover:text-gray-900 hover:bg-gray-500/20 transition-colors disabled:opacity-25 disabled:cursor-not-allowed"
                        title={
                          subQ.isLegacyFormat
                            ? 'Formato heredado — usa "Eliminar" y recrea con el formulario'
                            : subQ.isDragDrop
                            ? 'Drag & Drop no editable desde aquí'
                            : 'Editar pregunta'
                        }
                      >
                        <Pencil className="w-4 h-4" />
                      </button>

                      {/* Eliminar — disponible para todos (elimina el registro DB completo) */}
                      <button
                        onClick={() =>
                          onDelete({
                            type: 'testQuestion',
                            id: subQ.dbRecordId,
                            name: subQ.question.slice(0, 60),
                            categoryId: subQ.dbCategoryId,
                          })
                        }
                        className="p-1.5 rounded-lg text-gray-800/60 hover:text-red-600 hover:bg-red-50 transition-colors"
                        title="Eliminar pregunta"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>

                  {/* Opciones */}
                  {!subQ.isDragDrop && subQ.answers.length > 0 && (
                    <div className="ml-9 grid sm:grid-cols-2 gap-1.5">
                      {subQ.answers.map((ans) => (
                        <div
                          key={ans.id}
                          className={[
                            'flex items-start gap-2 px-3 py-1.5 rounded-md text-xs border',
                            ans.isCorrect
                              ? 'bg-green-50 border-green-200 text-green-800 font-medium'
                              : 'bg-gray-50 border-gray-200 text-gray-600',
                          ].join(' ')}
                        >
                          {subQ.mode === 'multi' ? (
                            <CheckSquare
                              className={`w-3 h-3 flex-shrink-0 mt-0.5 ${ans.isCorrect ? 'text-green-500' : 'text-gray-400'}`}
                            />
                          ) : (
                            <Circle
                              className={`w-3 h-3 flex-shrink-0 mt-0.5 ${ans.isCorrect ? 'fill-green-500 text-green-500' : 'text-gray-400'}`}
                            />
                          )}
                          <span className="uppercase font-semibold mr-0.5 flex-shrink-0">{ans.id})</span>
                          <span>{ans.text}</span>
                        </div>
                      ))}
                    </div>
                  )}

                  {subQ.isDragDrop && (
                    <p className="ml-9 text-xs text-gray-800/50 italic">
                      Pregunta tipo Drag &amp; Drop — no se puede previsualizar en el panel.
                    </p>
                  )}

                  {!subQ.isDragDrop && subQ.answers.length === 0 && (
                    <p className="ml-9 text-xs text-gray-800/40 italic">
                      Sin opciones reconocibles en este registro.
                    </p>
                  )}
                </div>
              ))
            )}
          </div>
        </>
      )}
    </Card>
  );
};

export default TestsTab;
